/**
 * High-level wrapper that the popover calls to run one assistant turn.
 *
 * Responsibilities:
 *   - Sanitize prior conversation history (strip UI-only fields).
 *   - Assemble the OpenAI message stack: system prompt + per-turn context
 *     + recent history + new user message.
 *   - Build the tool registry for this turn (binds `ctx` so tools can call
 *     into shared dispatchers like `navigate`).
 *   - Forward to runGatewayTurn and return its result verbatim.
 */

import { runGatewayTurn } from "./agentRunner";
import { buildToolRegistry } from "./toolRegistry";
import { SYSTEM_PROMPT } from "./systemPrompt";
import { buildGatewayContext, buildSectionCatalog } from "./gatewayContext";
import gatewayTools from "./tools";
import { buildRunSkillTool } from "./tools/skills";
import * as api from "../api";

const RECENT_TURN_FLOOR = 12;

// Static system block. The backend attaches cache_control to the FIRST
// system message only, so the bulky section catalog must live here — the
// second (per-turn) system block is reprocessed on every request.
const STATIC_SYSTEM_PROMPT = `${SYSTEM_PROMPT}\n\n${buildSectionCatalog()}`;

function sanitizeHistory(history) {
  if (!Array.isArray(history)) return [];
  const cleaned = [];
  for (const entry of history) {
    if (!entry || typeof entry !== "object") continue;
    if (entry.role !== "user" && entry.role !== "assistant") continue;
    let content = typeof entry.content === "string" ? entry.content : "";
    if (entry.role === "assistant" && typeof entry.finalAnswerText === "string" && entry.finalAnswerText) {
      content = entry.finalAnswerText;
    }
    if (!content) continue;
    cleaned.push({ role: entry.role, content });
  }
  if (cleaned.length <= RECENT_TURN_FLOOR) return cleaned;
  // Collapse older turns into a one-line bullet summary.
  const olderCount = cleaned.length - RECENT_TURN_FLOOR;
  const older = cleaned.slice(0, olderCount);
  const recent = cleaned.slice(olderCount);
  const lines = older.map((m) => {
    const flat = m.content.replace(/\s+/g, " ").trim();
    const preview = flat.length > 120 ? `${flat.slice(0, 120)}…` : flat;
    return `- ${m.role === "user" ? "User" : "Assistant"}: ${preview}`;
  });
  let summary = lines.join("\n");
  if (summary.length > 1200) summary = `${summary.slice(0, 1200)}…`;
  return [
    {
      role: "user",
      content: `[Conversation summary — ${older.length} earlier turn(s) collapsed.]\n${summary}`,
    },
    ...recent,
  ];
}

/**
 * @param {Object} opts
 * @param {string} opts.userMessage
 * @param {Array}  [opts.history]
 * @param {Array}  [opts.imageDataUrls]  Optional data-URL image attachments
 *   for the current turn. When non-empty, the trailing user message is sent
 *   as an OpenAI-shape content-block array (text + image_url parts) so the
 *   model can see them. sanitizeHistory drops images from past turns —
 *   re-attach to send again.
 * @param {Object} opts.ctx        Shared tool context: { activeSection, user, navigate }
 * @param {AbortSignal} [opts.signal]
 * @param {(evt:any)=>void} [opts.onEvent]
 */
export async function runGatewayAssistantTurn({
  userMessage,
  history = [],
  imageDataUrls = [],
  ctx,
  signal,
  onEvent,
} = {}) {
  if (typeof userMessage !== "string" || !userMessage.trim()) {
    throw new Error("runGatewayAssistantTurn: userMessage required");
  }
  if (!ctx || typeof ctx !== "object") {
    throw new Error("runGatewayAssistantTurn: ctx required");
  }

  // Skills (BETA-31): fetched fresh every turn, same as the Go plant
  // assistant's prefetch — run_skill's `name` schema enumerates exactly what
  // this read returns, so the model is never shown a stale list. A failed
  // fetch just means no skills this turn, not a broken turn.
  let skillIndex = [];
  try {
    const { skills = [] } = await api.listAgentSkills();
    skillIndex = skills.filter((s) => s.enabled).map((s) => ({ name: s.name, whenToUse: s.whenToUse }));
  } catch {
    skillIndex = [];
  }
  const tools = [...gatewayTools, buildRunSkillTool(skillIndex)];
  const registry = buildToolRegistry(ctx, { tools });
  // run_skill dispatches its steps through this exact registry, so a gated
  // step (request_confirmation on a GATED_KINDS mutation) still raises its
  // card — see tools/skills.js.
  ctx.dispatch = registry.dispatch;

  // Multi-modal: when the popover supplies imageDataUrls, send the user
  // message as an OpenAI-shape content-block array. Otherwise keep the
  // plain-string form. Mirrors cseditor/session.js + flowSession.js so
  // every AI surface posts the same shape to the backend.
  const userContent =
    Array.isArray(imageDataUrls) && imageDataUrls.length > 0
      ? [
          { type: "text", text: userMessage.trim() },
          ...imageDataUrls.map((url) => ({
            type: "image_url",
            image_url: { url, detail: "auto" },
          })),
        ]
      : userMessage.trim();

  const messages = [
    { role: "system", content: STATIC_SYSTEM_PROMPT },
    {
      role: "system",
      content: buildGatewayContext({
        activeSection: ctx.activeSection,
        user: ctx.user,
        license: ctx.license,
        skills: skillIndex,
      }),
    },
    ...sanitizeHistory(history),
    { role: "user", content: userContent },
  ];
  return runGatewayTurn({ messages, registry, signal, onEvent });
}

export default runGatewayAssistantTurn;
